import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
    CheckCircle2, Clock, Package, Truck, XCircle, Tag, MapPin, ExternalLink,
    ArrowRight, Sparkles,
} from "lucide-react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";

const STATUS_META = {
    awaiting_approval: { label: "Aguarda aprovação", icon: Clock, color: "text-gold-deep", desc: "O produtor está a verificar o pedido." },
    stock_confirmed: { label: "Stock confirmado", icon: CheckCircle2, color: "text-vineyard", desc: "Stock garantido. Vai começar a preparação." },
    preparing: { label: "Em preparação", icon: Package, color: "text-wine", desc: "O produtor está a embalar com cuidado." },
    ready_to_ship: { label: "Pronto para envio", icon: Package, color: "text-wine", desc: "Encomenda embalada e pronta para a transportadora." },
    label_generated: { label: "Etiqueta gerada", icon: Tag, color: "text-gold-deep", desc: "Etiqueta de envio emitida." },
    picked_up: { label: "Recolhido pela transportadora", icon: Truck, color: "text-vineyard", desc: "A transportadora já recolheu a encomenda." },
    in_transit: { label: "Em trânsito", icon: Truck, color: "text-wine-dark", desc: "A caminho do destino." },
    out_for_delivery: { label: "Saiu para entrega", icon: Truck, color: "text-gold-deep", desc: "O carteiro está a caminho — fique atento." },
    delivered: { label: "Entregue", icon: CheckCircle2, color: "text-vineyard", desc: "Boas degustações!" },
    returned: { label: "Devolvido", icon: XCircle, color: "text-destructive", desc: "A encomenda foi devolvida." },
    cancelled: { label: "Cancelado", icon: XCircle, color: "text-wine-dark/60", desc: "Este envio foi cancelado." },
};

export default function TrackingPage() {
    const { code } = useParams();
    const [s, setS] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/track/${code}`)
            .then((r) => setS(r.data))
            .catch(() => setErr("Código de tracking não encontrado."));
    }, [code]);

    return (
        <>
            <Navbar />
            <section className="pt-28 pb-20 bg-cream min-h-[80vh]">
                <div className="container-edge max-w-4xl mx-auto">
                    {err ? (
                        <div className="bg-white rounded-md border border-gold-soft/30 p-12 text-center" data-testid="tracking-error">
                            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" strokeWidth={1.2} />
                            <h1 className="font-heading text-3xl text-wine-dark mb-2">Sem rastreio</h1>
                            <p className="text-sm text-wine-dark/60">{err}</p>
                            <Link to="/" className="btn-wine-outline text-xs mt-6 inline-block">Voltar à página inicial</Link>
                        </div>
                    ) : !s ? (
                        <p className="text-wine-dark/60 text-center label-overline">A carregar…</p>
                    ) : (
                        <TrackingContent s={s} />
                    )}
                </div>
            </section>
            <Footer />
        </>
    );
}

function TrackingContent({ s }) {
    const meta = STATUS_META[s.status] || STATUS_META.awaiting_approval;
    const Icon = meta.icon;
    const isDelivered = s.status === "delivered";
    const isCancelled = s.status === "cancelled" || s.status === "returned";

    return (
        <div className="space-y-6" data-testid="tracking-content">
            <div>
                <span className="label-overline text-wine/60">Rastreio · Caseirinho</span>
                <h1 className="font-heading text-4xl md:text-5xl text-wine-dark mt-2">Acompanhar entrega</h1>
                {s.tracking_code && <p className="font-mono text-sm text-wine-dark/60 mt-1">{s.tracking_code}</p>}
            </div>

            {/* Hero current status */}
            <div className={`rounded-md p-8 ${isDelivered ? "bg-vineyard text-cream" : isCancelled ? "bg-wine-dark/5" : "bg-wine-dark text-cream"}`} data-testid="current-status">
                <div className="flex items-start gap-5">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDelivered ? "bg-cream/15" : isCancelled ? "bg-wine-dark/10" : "bg-gold-soft/20"}`}>
                        <Icon className={`h-8 w-8 ${isDelivered ? "text-cream" : isCancelled ? "text-wine-dark/60" : "text-gold-soft"}`} strokeWidth={1.4} />
                    </div>
                    <div className="flex-1">
                        <span className={`label-overline ${isDelivered ? "text-cream/70" : isCancelled ? "text-wine-dark/55" : "text-gold-soft"}`}>Estado atual</span>
                        <h2 className={`font-heading text-3xl mt-1 ${isCancelled ? "text-wine-dark" : ""}`}>{s.status_label}</h2>
                        <p className={`text-sm mt-2 ${isDelivered ? "text-cream/75" : isCancelled ? "text-wine-dark/70" : "text-cream/75"}`}>{meta.desc}</p>
                        {isDelivered && s.delivered_at && (
                            <p className="text-xs text-cream/55 mt-2">Entregue a {new Date(s.delivered_at).toLocaleString("pt-PT")}</p>
                        )}
                        {!isDelivered && !isCancelled && s.estimated_delivery && (
                            <p className={`text-xs mt-2 ${isCancelled ? "text-wine-dark/55" : "text-cream/60"}`}>
                                Previsão de entrega: <strong>{new Date(s.estimated_delivery).toLocaleDateString("pt-PT")}</strong>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Carrier / Producer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard label="Produtor" icon={Sparkles}>
                    <p className="font-heading text-xl text-wine-dark">{s.producer_name}</p>
                    <p className="text-xs text-wine-dark/55 mt-1">Embalou esta encomenda com cuidado.</p>
                </InfoCard>
                <InfoCard label="Transportadora" icon={Truck}>
                    {s.carrier ? (
                        <>
                            <p className="font-heading text-xl text-wine-dark">{s.carrier_name}</p>
                            {s.tracking_url && (
                                <a href={s.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs label-overline text-wine hover:text-gold-deep mt-1">
                                    Ver no site da transportadora <ExternalLink className="h-3 w-3" />
                                </a>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-wine-dark/55">Ainda não atribuída.</p>
                    )}
                </InfoCard>
            </div>

            {/* Items + city */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {s.city && (
                    <InfoCard label="Destino" icon={MapPin}>
                        <p className="font-heading text-xl text-wine-dark">{s.city}</p>
                    </InfoCard>
                )}
                <InfoCard label="Itens" icon={Package}>
                    {s.items.map((it, i) => (
                        <p key={i} className="text-sm text-wine-dark">{it.quantity}× {it.name}</p>
                    ))}
                </InfoCard>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-6">
                <h3 className="font-heading text-2xl text-wine-dark mb-5">Histórico</h3>
                <ol className="relative border-l-2 border-wine-dark/10 ml-3">
                    {[...s.timeline].reverse().map((ev, i) => {
                        const evMeta = STATUS_META[ev.status] || {};
                        const EvIcon = evMeta.icon || Clock;
                        const isLatest = i === 0;
                        return (
                            <li key={i} className="ml-6 pb-6 last:pb-0" data-testid={`timeline-${ev.status}`}>
                                <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${isLatest ? "bg-wine text-cream" : "bg-cream text-wine-dark/50 border border-wine-dark/10"}`}>
                                    <EvIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                </span>
                                <div>
                                    <h4 className={`font-heading text-lg ${isLatest ? "text-wine-dark" : "text-wine-dark/65"}`}>{evMeta.label || ev.status_label || ev.status}</h4>
                                    <p className="text-xs text-wine-dark/55">{new Date(ev.at).toLocaleString("pt-PT")}</p>
                                    {ev.note && <p className="text-sm text-wine-dark/70 italic mt-1">"{ev.note}"</p>}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>

            <div className="text-center pt-2">
                <Link to="/conta" className="btn-wine-outline text-xs inline-flex items-center gap-2">
                    Ver as minhas encomendas <ArrowRight className="h-3 w-3" />
                </Link>
            </div>
        </div>
    );
}

function InfoCard({ label, icon: Icon, children }) {
    return (
        <div className="bg-white rounded-md border border-gold-soft/30 p-5">
            <span className="label-overline text-wine/60 inline-flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</span>
            <div className="mt-2">{children}</div>
        </div>
    );
}
